import React, { useCallback, useContext, useRef } from 'react';

import { CardHcdAvailabilityOutputDTO } from '@maiia/model/generated/model/api-patient/api-patient';

import { useRouter } from 'next/router';

import { cn } from '@kiiwi/ui';
import SearchContext from '@/components/templates/Search/SearchContext';
import useResponsive from '@/src/hooks/useResponsive';
import ProCardContainer from '@/components/organisms/ProCard/ProCardContainer';
import ProCardInfos from '@/components/organisms/ProCard/ProCardInfos';
import ProCardAvailabilities from '@/components/organisms/ProCard/ProCardAvailabilities';

interface ProCardProps {
  item: CardHcdAvailabilityOutputDTO;
  specialityName?: string;
  onClickInfos?: React.ReactEventHandler;
  onClickTimeSlot?: React.ReactEventHandler;
  onClickAvatar?: React.ReactEventHandler;
  onClickName?: React.ReactEventHandler;
  onClickCTA?: React.ReactEventHandler;
}

const ProCard = ({
  item,
  specialityName,
  onClickName,
  onClickAvatar,
  onClickInfos,
  onClickTimeSlot,
  onClickCTA,
}: ProCardProps) => {
  const { isMobile } = useResponsive();
  const router = useRouter();
  const ctx = useContext(SearchContext);
  const timeoutId = useRef<NodeJS.Timeout | undefined>();

  const { id, center: { id: centerId } = {}, url } = item;

  const goToHcdPage = useCallback(
    (e: React.MouseEvent) => {
      if (!url) return;

      router.push({
        pathname: url,
        query: { ...(centerId ? { centerId } : {}) },
      });

      e.stopPropagation();
    },
    [url, centerId],
  );

  const onMouseEnter = useCallback(() => {
    if (!ctx) return;
    ctx.setHighlightItemId(id);
    const fly = async () => {
      const FlyToInterpolator = (
        await import(
          'react-map-gl/dist/es6/utils/transition/viewport-fly-to-interpolator'
        )
      ).default;
      ctx.setMapViewport({
        longitude: item?.address?.longitude,
        latitude: item?.address?.latitude,
        zoom: 17,
        bearing: 0,
        pitch: 0,
        transitionDuration: 500,
        transitionInterpolator: new FlyToInterpolator(),
      });
    };
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
    }
    timeoutId.current = setTimeout(fly, 2000);
  }, [item]);

  const onMouseLeave = useCallback(() => {
    if (!ctx) return;
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
      timeoutId.current = undefined;
    }
    ctx.setMapViewport(ctx.mapViewportDefault);
    if (
      !(
        'markerRef' in item &&
        item?.markerRef &&
        (item.markerRef as any).current
      )
    )
      return;
    ctx.setHighlightItemId(null);
  }, [item, ctx?.mapViewportDefault]);

  return (
    <ProCardContainer
      className={cn(isMobile && 'flex-col')}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={isMobile ? goToHcdPage : undefined}
    >
      <ProCardInfos
        item={item}
        specialityName={specialityName}
        onClickInfos={onClickInfos}
        onClickName={onClickName}
        onClickAvatar={onClickAvatar}
        goToHcdPage={goToHcdPage}
      />

      <div className={cn(isMobile ? 'w-full' : 'w-[330px]')}>
        <ProCardAvailabilities
          item={item}
          goToHcdPage={goToHcdPage}
          onClickTimeSlot={onClickTimeSlot}
          onClickCTA={onClickCTA}
        />
      </div>
    </ProCardContainer>
  );
};

ProCard.displayName = 'ProCard';

export default React.memo(
  ProCard,
  (oldProps, newProps) => oldProps.item.id === newProps.item.id,
);
